import type { MapRef } from 'react-map-gl/maplibre';

import type { GeoJsonPoint } from '@/clients/types';
import type { MapMode } from './mapLayers';

export const SemanticZoomLevel = {
    NetworkOverview: 5,
    AreaOfInterest: 4,
    DistrictMeterArea: 3,
    Pipelines: 2,
    Fittings: 1,
} as const;

export type SemanticZoomLevel = (typeof SemanticZoomLevel)[keyof typeof SemanticZoomLevel];

export interface SemanticZoomMeta {
    level: SemanticZoomLevel;
    label: string;
    /** Inclusive lower bound (MapLibre zoom). */
    minZoom: number;
    /** Exclusive upper bound (MapLibre zoom). null = unbounded. */
    maxZoom: number | null;
    /** Representative MapLibre zoom used when flying to this level (= the band start). */
    representativeZoom: number;
    /**
     * The mid-band MapLibre zoom — an intermediate "half step" between this circle and the next.
     * Zooming to it changes the map zoom without changing which layers are visible; the semantic
     * zoom control shows it as the joining connector filled ~50%. For the innermost (Fittings)
     * level, which has no next circle, this is simply one level deeper than the band start.
     */
    midStop: number;
    /** Position in the horizontal zoom control: 1 = leftmost (widest view), 5 = rightmost (street level). */
    displayOrder: number;
}

/**
 * Stats-scoping threshold. Below AREA_FOCUS_MIN_ZOOM the whole client network is in view and the
 * Global Stats bar shows overall network figures; at or above it (i.e. from the Area of Interest
 * level inward) the bar scopes to the AOI/DMA under the map centre. This is the Area of Interest
 * band start — Area of Interest is now its own stepper level, not a sub-zone of Network Overview.
 */
export const AREA_FOCUS_MIN_ZOOM = 8.5;

/** Fly-to target for the AOI stats card's "View Area of Interest" action — the AOI band mid-stop. */
export const AREA_FOCUS_ZOOM = 9.75;

/**
 * The zoom Network Explorer opens at and returns to: the whole globe in view with the estate's
 * cluster bubbles legible. Far below the Network Overview band start, which is a single
 * client's whole network — the explorer's "network" is the planet.
 */
export const EXPLORER_WORLD_ZOOM = 1.6;

export const SEMANTIC_ZOOM_LEVELS: ReadonlyArray<SemanticZoomMeta> = [
    // Each level flies to the bottom of its band (representativeZoom = minZoom) so that clicking a
    // circle lands at the widest view of that level and empties its outgoing connector. midStop is
    // the mid-band intermediate half-step. Network Overview is given a finite [6.5, 8.5) band so the
    // connector fraction has a denominator; anything below 6.5 still resolves to Network Overview.
    {
        level: 5,
        label: 'Network Overview',
        minZoom: 6.5,
        maxZoom: 8.5,
        representativeZoom: 6.5,
        midStop: 7.5,
        displayOrder: 1,
    },
    {
        level: 4,
        label: 'Area of Interest',
        minZoom: 8.5,
        maxZoom: 11,
        representativeZoom: 8.5,
        midStop: 9.75,
        displayOrder: 2,
    },
    // Pipelines and Fittings hold off until street level (pipes at 16, fittings at 17.5) so the
    // dense network detail doesn't crowd in too soon. The District Meter Area level spans a wide
    // 11–16 "hold" band (DMAs + POIs only). Every midStop is the true midpoint of its band, so each
    // connector reads exactly 50% at the half-step.
    {
        level: 3,
        label: 'District Meter Area',
        minZoom: 11,
        maxZoom: 16,
        representativeZoom: 11,
        midStop: 13.5,
        displayOrder: 3,
    },
    {
        level: 2,
        label: 'Pipelines',
        minZoom: 16,
        maxZoom: 17.5,
        representativeZoom: 16,
        midStop: 16.75,
        displayOrder: 4,
    },
    {
        level: 1,
        label: 'Fittings',
        minZoom: 17.5,
        maxZoom: null,
        representativeZoom: 17.5,
        midStop: 18,
        displayOrder: 5,
    },
] as const;

const LEVEL_META = new Map<SemanticZoomLevel, SemanticZoomMeta>(SEMANTIC_ZOOM_LEVELS.map((m) => [m.level, m]));

// Sorted descending by minZoom for the resolution algorithm.
const LEVELS_DESC = [...SEMANTIC_ZOOM_LEVELS].sort((a, b) => b.minZoom - a.minZoom);

/**
 * The 10 discrete MapLibre zoom stops the +/- buttons snap through: each level's band start followed
 * by its mid-band intermediate. Ascending: [6.5, 7.5, 8.5, 9.75, 11, 13.5, 16, 16.75, 17.5, 18].
 */
export const SEMANTIC_ZOOM_STOPS: ReadonlyArray<number> = [...SEMANTIC_ZOOM_LEVELS]
    .sort((a, b) => a.minZoom - b.minZoom)
    .flatMap((m) => [m.representativeZoom, m.midStop]);

/**
 * Network Explorer's ladder: the single-client stops plus two outer ones below them — the world
 * view it opens at and a continental step — so the +/- buttons can walk out to the globe instead
 * of stalling at the Network Overview band start. The single-client ladder is untouched.
 */
export const EXPLORER_ZOOM_STOPS: ReadonlyArray<number> = [EXPLORER_WORLD_ZOOM, 4, ...SEMANTIC_ZOOM_STOPS];

function zoomStopsFor(mode: MapMode): ReadonlyArray<number> {
    return mode === 'network-explorer' ? EXPLORER_ZOOM_STOPS : SEMANTIC_ZOOM_STOPS;
}

/** The next zoom stop above `zoom`, or the innermost stop when already at/beyond it. */
export function nextSemanticZoomStop(zoom: number, mode: MapMode = 'find-and-fix'): number {
    const stops = zoomStopsFor(mode);
    for (const stop of stops) {
        if (stop > zoom + 1e-6) return stop;
    }
    return stops[stops.length - 1];
}

/** The next zoom stop below `zoom`, or the widest stop when already at/beyond it. */
export function prevSemanticZoomStop(zoom: number, mode: MapMode = 'find-and-fix'): number {
    const stops = zoomStopsFor(mode);
    for (let i = stops.length - 1; i >= 0; i--) {
        if (stops[i] < zoom - 1e-6) return stops[i];
    }
    return stops[0];
}

/**
 * How far the map zoom has progressed through a level's band, as a fraction in [0, 1]. The semantic
 * zoom control uses this to fill the connector leaving the active circle — it reads 0 at the band
 * start, ~0.5 at the mid-band intermediate, and approaches 1 at the next band boundary. Returns 0
 * for the innermost (unbounded) level, which has no outgoing connector.
 */
export function connectorFraction(meta: SemanticZoomMeta, zoom: number): number {
    if (meta.maxZoom === null) return 0;
    if (!Number.isFinite(zoom)) return 0;
    const frac = (zoom - meta.minZoom) / (meta.maxZoom - meta.minZoom);
    return Math.min(1, Math.max(0, frac));
}

/**
 * Returns the label to display for a semantic zoom level, accounting for the hosting product.
 * In SAVI mode the District Meter Area band shows Air Valves layers (the SAVI equivalent of POIs,
 * which first appear at this level), so that step is relabelled "Air Valves". All other levels and
 * Find & Fix mode use the default label.
 */
export function getSemanticZoomLabel(meta: SemanticZoomMeta, mode: MapMode): string {
    if (mode === 'savi' && meta.level === SemanticZoomLevel.DistrictMeterArea) return 'Air Valves';
    return meta.label;
}

/** Returns the metadata for a semantic zoom level. Throws for unknown values. */
export function getSemanticZoomMeta(level: SemanticZoomLevel): SemanticZoomMeta {
    const meta = LEVEL_META.get(level);
    if (!meta) throw new Error(`Unknown semantic zoom level: ${String(level)}`);
    return meta;
}

/**
 * Resolves a MapLibre zoom value to a semantic zoom level using threshold-based range matching.
 * - Values below the Network Overview band (< 6.5) clamp to NetworkOverview.
 * - Values at or above the Fittings band start (>= 17.5) clamp to Fittings.
 * - Non-finite values: -Infinity and NaN clamp to NetworkOverview; +Infinity clamps to Fittings.
 */
export function mapLibreZoomToSemanticZoomLevel(mapLibreZoom: number): SemanticZoomLevel {
    if (!Number.isFinite(mapLibreZoom)) {
        return mapLibreZoom > 0 ? SemanticZoomLevel.Fittings : SemanticZoomLevel.NetworkOverview;
    }
    for (const meta of LEVELS_DESC) {
        if (mapLibreZoom >= meta.minZoom) return meta.level;
    }
    return SemanticZoomLevel.NetworkOverview;
}

/** Returns the representative MapLibre zoom for a semantic zoom level. */
export function semanticZoomLevelToMapLibreZoom(level: SemanticZoomLevel): number {
    return getSemanticZoomMeta(level).representativeZoom;
}

/**
 * Flies the map to a client's network focal point at the Network Overview semantic zoom level.
 * Shared by the client-switch fly-to (`useMapUrlSync`) and the Locate Network control.
 *
 * The semantic-zoom stepper updates itself off the map's `moveend` event (which `flyTo` fires),
 * so callers do not need to set the semantic level manually.
 *
 * @returns `true` when the fly-to was issued; `false` when there is no map or no focal point
 *          (lets callers decide whether to surface feedback to the user).
 */
export function flyToClientNetwork(map: MapRef | null, focalPoint?: GeoJsonPoint | null): boolean {
    if (!map || !focalPoint) return false;
    const target = clientNetworkViewport(focalPoint);
    if (!target) return false;
    map.flyTo({ center: [target.longitude, target.latitude], zoom: target.zoom });
    return true;
}

/**
 * The camera {@link flyToClientNetwork} lands on, as a viewport a map can open at directly:
 * the client's focal point at the Network Overview zoom, level and north-up. A map that has
 * no remembered viewport to restore opens here rather than on a fixed default and flying,
 * so the client is framed from the first frame. Undefined when the client has no focal point.
 */
export function clientNetworkViewport(
    focalPoint?: GeoJsonPoint | null,
): { longitude: number; latitude: number; zoom: number; bearing: number; pitch: number } | undefined {
    if (!focalPoint) return undefined;
    const [longitude, latitude] = focalPoint.coordinates;
    return {
        longitude,
        latitude,
        zoom: semanticZoomLevelToMapLibreZoom(SemanticZoomLevel.NetworkOverview),
        bearing: 0,
        pitch: 0,
    };
}

/**
 * Network Explorer's counterpart to {@link flyToClientNetwork}: back out to the world view the
 * explorer opens at. There is no client and no focal point to depend on, so the only way this
 * can decline is when there is no map yet.
 */
export function flyToExplorerWorldView(
    map: MapRef | null,
    view: { longitude: number; latitude: number; zoom: number },
): boolean {
    if (!map) return false;
    map.flyTo({ center: [view.longitude, view.latitude], zoom: view.zoom, pitch: 0, bearing: 0 });
    return true;
}
